"use client"

import * as React from "react"
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger, BadgeProps as ShadBadgeProps } from "@ui/shad"
import { Clock } from "lucide-react"

import { TimeSelect } from "../time-picker/time-select"

export interface BadgeDateProps extends ShadBadgeProps {
  value?: Date | null
  onValueChange: (value: Date | null) => void
  disabled?: boolean
  showTime?: boolean
  setShowTime?: (showTime: boolean) => void
  includeTime?: boolean
}

export function BadgeInputDate({
  children,
  disabled = false,
  value,
  onValueChange,
  showTime = false,
  setShowTime,
  includeTime = false,
}: BadgeDateProps) {
  const [localTimeValue, setLocalTimeValue] = React.useState<string | null>(null)

  const handleTimeChange = (timeString: string) => {
    if (timeString === "none") {
      if (value) {
        const newDate = new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0)
        setLocalTimeValue("none")
        onValueChange(newDate)
      }
      setShowTime?.(false)
      return
    }
    const [hours, minutes] = timeString.split(":")
    if (!value) {
      const today = new Date()
      today.setHours(parseInt(hours), parseInt(minutes), 0, 0)
      setLocalTimeValue(timeString)
      onValueChange(today)
    } else {
      const newDate = new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        parseInt(hours),
        parseInt(minutes),
        0,
        0
      )
      setLocalTimeValue(timeString)
      onValueChange(newDate)
    }
    setShowTime?.(true)
  }

  const getTimeValue = (): string => {
    if (localTimeValue !== null) return localTimeValue
    if (!showTime || !value) return "none"
    const h = value.getHours()
    const m = value.getMinutes()
    if (h === 0 && m === 0) return "none"
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  const [open, setOpen] = React.useState(false)

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setLocalTimeValue(null)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`gap-4 ${value ? "default" : "border-dashed"} flex h-fit w-fit max-w-72 items-center justify-end px-2`}
          size="lg"
          disabled={disabled}
          type="button"
        >
          {children}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={(date: Date | undefined) => {
            if (!date) {
              onValueChange(null)
              return
            }
            if (value) {
              date.setHours(value.getHours(), value.getMinutes())
            }
            onValueChange(date)
            setOpen(false)
          }}
          disabled={disabled}
          initialFocus
        />
        {includeTime && (
          <div className="border-border border-t px-3 py-2">
            <div className="flex h-7 items-center gap-2">
              <Clock className="h-4 w-4 opacity-70" />
              <TimeSelect
                value={getTimeValue()}
                onChange={handleTimeChange}
                disabled={disabled}
                size="xs"
                includeNone
              />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
